import React, { useState } from 'react';
import { Mic, X } from 'lucide-react';
import { neonQuery } from '../services/api';

// The capture button and its sheet, in one place so the Dashboard and the To-Do page
// cannot drift apart. This repo has been bitten more than once by two near-identical
// copies of the same thing.
//
// What lands here is untriaged: it carries triaged_at NULL, and v_work_open filters
// those out, so a thought caught on the move is on nobody list and in nobody brief
// until it is placed by hand.
//
// There is deliberately no voice code. webkitSpeechRecognition is Chrome and Edge only
// and does not work in an iOS PWA, and the iOS keyboard microphone already dictates
// into a focused text box better than anything we would build.

export default function QuickCapture({ onSaved, style, label = 'Capture', title = 'Capture a thought' }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true); setErr('');
    try {
      await neonQuery('greg_capture_add', { text: t, source: 'cpx' });
      setText('');
      setOpen(false);           // one thought, one save, out of the way
      if (onSaved) onSaved();
    } catch (e) {
      // Keep what they typed. Losing a captured thought is the one unforgivable bug here.
      setErr('That did not save. Try again in a moment.');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => { setErr(''); setOpen(true); }} title={title}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
          background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 20,
          fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
          ...(style || {}),
        }}>
        <Mic size={14} /> {label}
      </button>

      {open && (
        <div onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,20,40,.5)', zIndex: 95, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 10, width: 'min(560px,100%)', marginTop: '8vh', padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)' }}>What is on your mind?</div>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)' }}>
                <X size={18} />
              </button>
            </div>
            <textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); } }}
              placeholder="Say it or type it. It goes nowhere near anyone until you place it."
              rows={4}
              // 16px or larger, or iOS Safari zooms the whole page when the field focuses.
              style={{ width: '100%', padding: 12, border: '1px solid var(--gray-300)', borderRadius: 8, fontSize: 16, fontFamily: 'inherit', resize: 'none', outline: 'none', lineHeight: 1.5 }}
            />
            {err && <div style={{ marginTop: 8, fontSize: 12.5, color: '#a12020' }}>{err}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
              <button onClick={save} disabled={!text.trim() || busy}
                style={{ padding: '9px 18px', background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: (!text.trim() || busy) ? 0.5 : 1 }}>
                {busy ? 'Saving...' : 'Save'}
              </button>
              <div style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>
                Tap the microphone on your keyboard to talk.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
